/**
 * Catálogo das sagas registradas no backend `SagaRegistry` —
 * espelha as `*Input` interfaces em `backend/src/saga/sagas/*.saga.ts`
 * e `backend/src/saga/test-sagas.ts`.
 *
 * Mantido como cópia consciente (sem package shared no MVP).
 * Quando uma saga nova for adicionada no backend, esta lista DEVE ser atualizada.
 */

export interface SagaCatalogEntry {
  /** Nome registrado no SagaRegistry — mesma string que vai em `sagaType`. */
  name: string;
  /** Descrição curta — 1 frase, mostrada abaixo do select. */
  description: string;
  /** Exemplo de input válido. Stringificado pra preencher o textarea. */
  example: unknown;
  /** Campos esperados (para help inline). */
  fields: ReadonlyArray<{ name: string; type: string; required: boolean; note?: string }>;
}

/**
 * Helper para gerar tenantKey único — evita o usuário disparar 2 vezes
 * a mesma saga e bater em idempotência ou conflict.
 */
function uniqueTenantKey(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}`;
}

export const SAGA_CATALOG: ReadonlyArray<SagaCatalogEntry> = [
  {
    name: "EchoSaga",
    description: "Saga de teste — 2 steps que apenas ecoam o input. Não toca KC/DB. Útil pra validar o pipeline da API + persistência.",
    example: { hello: "world" },
    fields: [{ name: "(qualquer)", type: "any JSON", required: false, note: "Input é só ecoado nos outputs" }],
  },
  {
    name: "FailingTestSaga",
    description: "Saga de teste — step 0 OK, step 1 falha sempre, exercita compensation reversa. Termina em FAILED ou FAILED_NEEDS_MANUAL.",
    example: { foo: 1 },
    fields: [{ name: "(qualquer)", type: "any JSON", required: false }],
  },
  {
    name: "OnboardTenant",
    description: "Cria tenant novo (8 steps): realm KC + DB tenant + DNS + S3 + branding + notifications + client + record local. Compensation reversa em failure.",
    example: {
      tenantKey: uniqueTenantKey("cliente"),
      name: "Cliente Exemplo",
      billingTier: "free",
      brandingColor: "#475569",
    },
    fields: [
      { name: "tenantKey", type: "string", required: true, note: "kebab-case, único por tenant" },
      { name: "name", type: "string", required: true, note: "Nome legível (display)" },
      { name: "billingTier", type: '"free" | "starter" | "pro" | "enterprise"', required: true },
      { name: "brandingColor", type: "string (hex)", required: false, note: "Default slate-700 #475569" },
    ],
  },
  {
    name: "ArchiveTenant",
    description: "Soft-delete: status → ARCHIVED, suspende realm KC, aponta DNS pra offline-page. Reversível via RestoreTenant.",
    example: { tenantKey: "cliente-existente" },
    fields: [
      { name: "tenantKey", type: "string", required: true, note: "Tenant deve estar em status ACTIVE" },
    ],
  },
  {
    name: "RestoreTenant",
    description: "Reverte ArchiveTenant: status → ACTIVE, resume realm KC, restaura DNS pro subdomínio normal.",
    example: { tenantKey: "cliente-archived" },
    fields: [
      { name: "tenantKey", type: "string", required: true, note: "Tenant deve estar em status ARCHIVED" },
    ],
  },
  {
    name: "DeleteTenantPermanently",
    description: "HARD-DELETE irreversível: deleta realm KC, dropa DB tenant, deleta bucket S3, remove DNS, apaga registros locais. Sem rollback.",
    example: { tenantKey: "cliente-archived" },
    fields: [
      { name: "tenantKey", type: "string", required: true, note: "Precisa estar ARCHIVED + header X-Confirm-Delete: yes" },
    ],
  },
  {
    name: "MigrateTenantTier",
    description: "Mudar billing-tier (free/starter/pro/enterprise). Ajusta connection pool size do DB tenant + atributos do realm KC.",
    example: { tenantKey: "cliente-existente", targetTier: "pro" },
    fields: [
      { name: "tenantKey", type: "string", required: true, note: "Tenant deve estar em status ACTIVE" },
      { name: "targetTier", type: '"free" | "starter" | "pro" | "enterprise"', required: true },
    ],
  },
  {
    name: "RotateTenantSecrets",
    description: "Rotaciona credenciais do tenant: client_secret no KC + senha do user app do DB tenant. Mantém old secret válido por grace period.",
    example: { tenantKey: "cliente-existente" },
    fields: [
      { name: "tenantKey", type: "string", required: true, note: "Tenant deve estar em status ACTIVE" },
    ],
  },
  {
    name: "CloneTenantForStaging",
    description: "Clona tenant existente pra realm de staging (com TTL automático via StagingCloneExpiryScheduler). 6 steps + compensation reversa.",
    example: { sourceTenantKey: "cliente-existente", ttlDays: 7 },
    fields: [
      { name: "sourceTenantKey", type: "string", required: true, note: "Tenant fonte (ACTIVE)" },
      { name: "ttlDays", type: "number", required: false, note: "Default 7. Após esse tempo, scheduler dispara DeleteTenantPermanently" },
    ],
  },
];

/** Indexa o catálogo por nome — `SAGA_BY_NAME["OnboardTenant"]?.example` */
export const SAGA_BY_NAME: Readonly<Record<string, SagaCatalogEntry>> = Object.fromEntries(
  SAGA_CATALOG.map((s) => [s.name, s]),
);

/** Stringifica o exemplo com indent 2 — pronto pra colar no textarea. */
export function exampleAsJson(name: string): string {
  const entry = SAGA_BY_NAME[name];
  if (!entry) return "{\n  \n}";
  return JSON.stringify(entry.example, null, 2);
}
